import { useMemo, useRef, useEffect } from "react";
import { initials, colorFor, displayName } from "@/lib/avatar";
import { AlertTriangle } from "lucide-react";

// Layout constants (kept symmetrical with GanttChart for visual consistency)
const DAY_WIDTH = 28;
const ROW_HEIGHT = 36;
const ROW_GAP = 4;
const SWIMLANE_HEADER = 28;
const SWIMLANE_PADDING_TOP = 8;
const SWIMLANE_PADDING_BOTTOM = 12;
const HEADER_HEIGHT = 52;
const LEFT_GUTTER = 240;

const PHASE_COLOR = {
  pre:  "#14b8a6",
  prod: "#f59e0b",
  post: "#a78bfa",
};

// Date helpers — duplicated from GanttChart by design (each chart owns its
// rendering math; sharing tempts coupling).
const parseDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
};
const addDays = (d, n) => {
  const x = new Date(d.getTime());
  x.setUTCDate(x.getUTCDate() + n);
  return x;
};
const daysBetween = (a, b) => Math.round((b - a) / (24 * 60 * 60 * 1000));
const isoOf = (d) => d.toISOString().slice(0, 10);
const fmtMonth = (d) =>
  d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

/**
 * Resource (assignee) timeline. Rows = team members; bars = their tasks across
 * any phase. Over-allocation bands in amber underlay flag days where the
 * person's hours > working_hours_per_day.
 *
 * Unassigned tasks land in a single "Unassigned" lane at the bottom so they
 * don't disappear.
 */
export default function ResourceView({
  plan,
  tasks,
  members = [],
  allocations = {},
  onTaskClick,
}) {
  const scrollRef = useRef(null);
  const hoursPerDay = plan?.working_hours_per_day || 8;

  // Group tasks by assignee. Build user list from BOTH actual assignees in
  // tasks AND the project member list, so empty members still get a row.
  const lanes = useMemo(() => {
    const memberById = Object.fromEntries(members.map((m) => [m.user_id, m]));
    const grouped = {};
    tasks.forEach((t) => {
      const uid = t.assigned_user_id || "__unassigned__";
      (grouped[uid] = grouped[uid] || []).push(t);
    });
    Object.values(grouped).forEach((list) =>
      list.sort((a, b) => (a.start || "").localeCompare(b.start || "")),
    );
    // Order: assigned members alphabetically, then unassigned last
    const memberIds = Object.keys(grouped).filter((k) => k !== "__unassigned__");
    memberIds.sort((a, b) => {
      const an = displayName(memberById[a] || { user_id: a }).toLowerCase();
      const bn = displayName(memberById[b] || { user_id: b }).toLowerCase();
      return an.localeCompare(bn);
    });
    const out = memberIds.map((uid) => ({
      key: uid,
      label: displayName(memberById[uid] || { name: "Unknown user" }),
      color: colorFor(uid),
      initials: initials(memberById[uid]?.name || memberById[uid]?.email || "?"),
      tasks: grouped[uid] || [],
      isUnassigned: false,
    }));
    if (grouped["__unassigned__"]) {
      out.push({
        key: "__unassigned__",
        label: "Unassigned",
        color: "#52525b",
        initials: "—",
        tasks: grouped["__unassigned__"],
        isUnassigned: true,
      });
    }
    return out;
  }, [tasks, members]);

  // Timeline window
  const { startDate, totalDays } = useMemo(() => {
    const dates = [];
    tasks.forEach((t) => {
      const s = parseDate(t.start);
      const e = parseDate(t.end || t.start);
      if (s) dates.push(s);
      if (e) dates.push(e);
    });
    let start;
    let end;
    if (dates.length === 0) {
      start = new Date();
      start.setUTCHours(0, 0, 0, 0);
      end = addDays(start, 30);
    } else {
      start = new Date(Math.min(...dates.map((d) => d.getTime())));
      end = new Date(Math.max(...dates.map((d) => d.getTime())));
    }
    start = addDays(start, -5);
    end = addDays(end, 10);
    return { startDate: start, endDate: end, totalDays: daysBetween(start, end) + 1 };
  }, [tasks]);

  const workingDays = plan?.working_days || [1, 2, 3, 4, 5];
  const holidaysSet = useMemo(() => new Set(plan?.holidays || []), [plan?.holidays]);
  const isOffDay = (d) => {
    const dow = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    if (!workingDays.includes(dow)) return true;
    if (holidaysSet.has(isoOf(d))) return true;
    return false;
  };

  // Auto-scroll to today
  useEffect(() => {
    if (!scrollRef.current) return;
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const offset = daysBetween(startDate, today);
    if (offset > 0 && offset < totalDays) {
      scrollRef.current.scrollLeft = Math.max(0, offset * DAY_WIDTH - 200);
    }
  }, [startDate, totalDays]);

  // Lane heights
  const laneHeights = lanes.map((l) => {
    const n = Math.max(l.tasks.length, 1);
    return SWIMLANE_HEADER + SWIMLANE_PADDING_TOP + n * (ROW_HEIGHT + ROW_GAP) + SWIMLANE_PADDING_BOTTOM;
  });
  const totalHeight = HEADER_HEIGHT + laneHeights.reduce((a, b) => a + b, 0);
  const totalWidth = LEFT_GUTTER + totalDays * DAY_WIDTH;

  const monthBands = useMemo(() => {
    const bands = [];
    let cursor = new Date(startDate.getTime());
    let bandStart = 0;
    let currentLabel = fmtMonth(cursor);
    for (let i = 0; i < totalDays; i++) {
      const lbl = fmtMonth(cursor);
      if (lbl !== currentLabel) {
        bands.push({ label: currentLabel, start: bandStart, width: i - bandStart });
        bandStart = i;
        currentLabel = lbl;
      }
      cursor = addDays(cursor, 1);
    }
    bands.push({ label: currentLabel, start: bandStart, width: totalDays - bandStart });
    return bands;
  }, [startDate, totalDays]);

  if (lanes.length === 0) {
    return (
      <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center">
        <p className="text-zinc-500 text-sm">
          No assignments yet. Assign tasks to team members from the Gantt to see their load here.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-950" data-testid="resource-view">
      <div ref={scrollRef} className="overflow-auto" style={{ maxHeight: "70vh" }}>
        <svg
          width={totalWidth}
          height={totalHeight}
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: "block", fontFamily: "inherit", userSelect: "none" }}
          data-testid="resource-svg"
        >
          {/* Background grid */}
          <g>
            {Array.from({ length: totalDays }).map((_, i) => {
              const d = addDays(startDate, i);
              const x = LEFT_GUTTER + i * DAY_WIDTH;
              const off = isOffDay(d);
              const todayObj = new Date();
              const today =
                d.getUTCFullYear() === todayObj.getUTCFullYear() &&
                d.getUTCMonth() === todayObj.getUTCMonth() &&
                d.getUTCDate() === todayObj.getUTCDate();
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={HEADER_HEIGHT}
                    width={DAY_WIDTH}
                    height={totalHeight - HEADER_HEIGHT}
                    fill={off ? "#0a0a0a" : "transparent"}
                    stroke="#27272a"
                    strokeWidth={i === 0 ? 0 : 0.5}
                  />
                  {today && (
                    <line
                      x1={x + DAY_WIDTH / 2}
                      y1={HEADER_HEIGHT}
                      x2={x + DAY_WIDTH / 2}
                      y2={totalHeight}
                      stroke="#f59e0b"
                      strokeWidth={1.5}
                      strokeDasharray="3,3"
                      opacity={0.7}
                    />
                  )}
                </g>
              );
            })}
          </g>

          {/* Header */}
          <g>
            <rect x={0} y={0} width={totalWidth} height={HEADER_HEIGHT} fill="#09090b" />
            <rect x={0} y={0} width={LEFT_GUTTER} height={HEADER_HEIGHT} fill="#0a0a0a" />
            <line x1={0} y1={HEADER_HEIGHT} x2={totalWidth} y2={HEADER_HEIGHT} stroke="#3f3f46" />
            <line x1={LEFT_GUTTER} y1={0} x2={LEFT_GUTTER} y2={totalHeight} stroke="#3f3f46" />
            {monthBands.map((b, i) => (
              <g key={i}>
                <rect
                  x={LEFT_GUTTER + b.start * DAY_WIDTH}
                  y={0}
                  width={b.width * DAY_WIDTH}
                  height={26}
                  fill={i % 2 === 0 ? "#0a0a0a" : "#0e0e10"}
                />
                <text
                  x={LEFT_GUTTER + b.start * DAY_WIDTH + 8}
                  y={17}
                  fill="#a1a1aa"
                  fontSize={11}
                  fontWeight={500}
                  letterSpacing={0.5}
                >
                  {b.label.toUpperCase()}
                </text>
              </g>
            ))}
            {Array.from({ length: totalDays }).map((_, i) => {
              const d = addDays(startDate, i);
              const x = LEFT_GUTTER + i * DAY_WIDTH;
              return (
                <text
                  key={i}
                  x={x + DAY_WIDTH / 2}
                  y={42}
                  fill={isOffDay(d) ? "#52525b" : "#a1a1aa"}
                  fontSize={10}
                  textAnchor="middle"
                >
                  {d.getUTCDate()}
                </text>
              );
            })}
          </g>

          {/* Lanes */}
          {(() => {
            let yCursor = HEADER_HEIGHT;
            return lanes.map((lane, laneIdx) => {
              const laneY = yCursor;
              const laneHeight = laneHeights[laneIdx];
              yCursor += laneHeight;
              const userLoad = allocations[lane.key] || {};
              const overAllocatedDays = Object.keys(userLoad).filter(
                (iso) => (userLoad[iso] || 0) > hoursPerDay,
              );
              return (
                <g key={lane.key} data-testid={`resource-lane-${lane.key}`}>
                  <line x1={0} y1={laneY} x2={totalWidth} y2={laneY} stroke="#27272a" />
                  {/* Lane header */}
                  <rect x={0} y={laneY} width={LEFT_GUTTER} height={SWIMLANE_HEADER} fill="#09090b" />
                  {/* Avatar circle */}
                  <circle
                    cx={20}
                    cy={laneY + SWIMLANE_HEADER / 2}
                    r={10}
                    fill={lane.isUnassigned ? "transparent" : lane.color}
                    stroke={lane.isUnassigned ? "#3f3f46" : "transparent"}
                    strokeWidth={lane.isUnassigned ? 1.5 : 0}
                    strokeDasharray={lane.isUnassigned ? "3,2" : ""}
                  />
                  <text
                    x={20}
                    y={laneY + SWIMLANE_HEADER / 2 + 3}
                    fill={lane.isUnassigned ? "#71717a" : "#09090b"}
                    fontSize={9}
                    fontWeight={700}
                    textAnchor="middle"
                  >
                    {lane.initials}
                  </text>
                  <text x={42} y={laneY + 18} fill="#e4e4e7" fontSize={12} fontWeight={500}>
                    {lane.label.length > 22 ? lane.label.slice(0, 21) + "…" : lane.label}
                  </text>
                  {/* Task count + over-alloc warning */}
                  <text x={LEFT_GUTTER - 14} y={laneY + 18} fill="#52525b" fontSize={10} textAnchor="end">
                    {lane.tasks.length} {lane.tasks.length === 1 ? "task" : "tasks"}
                    {overAllocatedDays.length > 0 && (
                      <tspan fill="#fb923c" fontWeight={700}>
                        {"  ·  "}
                        {overAllocatedDays.length} over-allocated
                      </tspan>
                    )}
                  </text>

                  {/* Over-allocation amber underlay bands */}
                  {overAllocatedDays.map((iso) => {
                    const d = parseDate(iso);
                    if (!d) return null;
                    const col = daysBetween(startDate, d);
                    if (col < 0 || col >= totalDays) return null;
                    const ratio = (userLoad[iso] || 0) / hoursPerDay;
                    return (
                      <g key={iso}>
                        <rect
                          x={LEFT_GUTTER + col * DAY_WIDTH}
                          y={laneY + SWIMLANE_HEADER}
                          width={DAY_WIDTH}
                          height={laneHeight - SWIMLANE_HEADER}
                          fill="#fb923c"
                          opacity={0.12}
                        />
                        <title>{`${ratio.toFixed(1)}× capacity (${userLoad[iso]}h vs ${hoursPerDay}h)`}</title>
                      </g>
                    );
                  })}

                  {/* Task bars */}
                  {lane.tasks.map((task, idx) => {
                    const taskStart = parseDate(task.start);
                    const taskEnd = parseDate(task.end || task.start);
                    if (!taskStart) return null;
                    const startCol = daysBetween(startDate, taskStart);
                    const endCol = taskEnd ? daysBetween(startDate, taskEnd) : startCol;
                    const subDay = task.duration < 1;
                    const widthDays = subDay ? task.duration : Math.max(1, endCol - startCol + 1);
                    const x = LEFT_GUTTER + startCol * DAY_WIDTH;
                    const w = widthDays * DAY_WIDTH - 2;
                    const rowY = laneY + SWIMLANE_HEADER + SWIMLANE_PADDING_TOP + idx * (ROW_HEIGHT + ROW_GAP);
                    const barY = rowY + 4;
                    const barH = ROW_HEIGHT - 8;
                    return (
                      <g
                        key={task.id}
                        data-testid={`resource-task-${task.id}`}
                        onClick={() => onTaskClick && onTaskClick(task)}
                        style={{ cursor: onTaskClick ? "pointer" : "default" }}
                      >
                        <rect
                          x={x}
                          y={barY}
                          width={w}
                          height={barH}
                          rx={3}
                          ry={3}
                          fill={PHASE_COLOR[task.phase] || "#71717a"}
                          opacity={0.85}
                        />
                        <text
                          x={x + 8}
                          y={barY + barH / 2 + 4}
                          fill="#09090b"
                          fontSize={11}
                          fontWeight={600}
                          clipPath={`inset(0 ${Math.max(0, w - 100)}px 0 0)`}
                        >
                          {truncate(task.name, Math.max(4, Math.floor(w / 8)))}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            });
          })()}
        </svg>
      </div>
      {/* Legend / summary */}
      <div className="border-t border-zinc-800 bg-zinc-950/80 px-4 py-2 flex items-center gap-4 text-xs text-zinc-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-sm bg-teal-500" /> Pre
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-sm bg-amber-500" /> Prod
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-sm bg-violet-500" /> Post
        </span>
        <span className="inline-flex items-center gap-1.5 ml-auto">
          <AlertTriangle className="w-3 h-3 text-orange-400" />
          <span className="text-orange-300/80">Amber underlay = over capacity that day</span>
        </span>
      </div>
    </div>
  );
}

function truncate(str, n) {
  if (!str) return "";
  if (str.length <= n) return str;
  return str.slice(0, Math.max(0, n - 1)) + "…";
}
